import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Marker, LayerGroup } from "react-leaflet";
import { SegmentIcon, DatumIcon, SignalIcon } from "./Icons";
import { LazyMapTooltip } from "../util/LazyTooltips";
import { locationsAreEqual } from "../util/Geometry";
import { routeSelected } from "redux/actions/index";
import _ from "lodash";
import { coordinateToPlaylistDistance } from "components/util/GeometryUtilsV2";
import { routeGeomSelector } from "redux/selectors";

const routeDatumSelector = (state) => state.featureOverlay.route;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const featuresSelector = (state) => state.featureOverlay.features;
const datumSelector = (state) => _.get(state.featureOverlay, ["datum"], null);
const userConfigSelector = (state) => state.userDetails.userConfig;

function FeatureMarkerTooltip({ feature, isDatum }) {
    const latLon = useMemo(() => {
        return `${parseFloat(feature.location[1]).toFixed(5)}, ${parseFloat(feature.location[0]).toFixed(5)}`;
    }, [feature.location]);

    const datumInformation = useSelector(routeDatumSelector);
    const userConfig = useSelector(userConfigSelector);
    const elrUnits = useSelector(elrUnitsSelector);
    const routeGeometryData = useSelector(routeGeomSelector);

    const distanceFromDatumInfo = useMemo(() => {
        if (isDatum) {
            return "Datum Point";
        } else {
            if (!datumInformation) {
                return null;
            }

            const currentDistance = coordinateToPlaylistDistance(feature.location, _.get(routeGeometryData, ["distance_to_coordinate"]));
            let distanceFromDatum = Math.abs(currentDistance - datumInformation.playlistDistance);

            if (distanceFromDatum < 10) {
                distanceFromDatum = Math.round(distanceFromDatum * 10) / 10;
            } else {
                distanceFromDatum = Math.round(distanceFromDatum);
            }
            const measurementUnit = userConfig.measurement_units;

            if (measurementUnit === "metres") {
                return `${distanceFromDatum}m from datum`;
            } else if (measurementUnit === "yards") {
                let yardValue = distanceFromDatum * 1.0936132983;
                if (yardValue < 10) {
                    yardValue = Math.round(yardValue * 10) / 10;
                } else {
                    yardValue = Math.round(yardValue);
                }
                return `${yardValue}y from datum`;
            }
        }
    }, [isDatum, datumInformation, feature.location, routeGeometryData, userConfig.measurement_units]);

    let closestELRInfo = null;
    if (feature.routePosition) {
        closestELRInfo = feature.routePosition.to_string(elrUnits, []);
    }

    return (
        <LazyMapTooltip
            className="Incident-Tooltip"
            permanent={false}
            direction={"top"}
            offset={[0, -35]}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <span style={{ margin: 2.5 }}>Type: {feature.type}</span>
                {distanceFromDatumInfo && <span style={{ margin: 2.5 }}>{distanceFromDatumInfo}</span>}
                <span style={{ margin: 2.5 }}>Lat/lon: {latLon}</span>
                {closestELRInfo && <span style={{ margin: 2.5 }}>{closestELRInfo}</span>}
            </div>
        </LazyMapTooltip>
    );
}

function FeatureMarker({ feature, ...props }) {
    const dispatch = useDispatch();
    const datum = useSelector(datumSelector);

    const onClick = () => {
        dispatch(routeSelected(feature.session_id, feature.session_ts - 1));
    };

    const isDatum = useMemo(() => {
        if (!datum) {
            return false;
        }
        return locationsAreEqual(feature.location, datum.location);
    }, [feature.location, datum]);

    let icon;
    if (isDatum) {
        icon = DatumIcon;
    } else if (feature.type === "signal") {
        icon = SignalIcon;
    } else {
        icon = SegmentIcon;
    }

    return (
        <Marker
            {...props}
            onClick={onClick}
            position={[feature.location[1], feature.location[0]]}
            icon={icon}>
            <FeatureMarkerTooltip
                feature={feature}
                isDatum={isDatum}
            />
        </Marker>
    );
}

export default function FeatureMarkers() {
    const features = useSelector(featuresSelector);

    const featureMarkers = useMemo(() => {
        return features.map((feature, idx) => (
            <FeatureMarker
                feature={feature}
                key={`feature${idx}`}
            />
        ));
    }, [features]);

    return <LayerGroup>{featureMarkers}</LayerGroup>;
}
